/**
 * System Settings (Admin)
 *
 * Real-data only:
 * - reads from /api/v1/admin/settings/*
 * - writes only maintenance mode toggle
 */

import type { ReactNode } from "react";
import { useMemo } from "react";
import { useAuthContext } from "@/contexts/AuthContext";
import { AdminLayout } from "../../layout/AdminLayout";
import { EnterprisePageHeader } from "../../components/layout/EnterprisePageHeader";
import { useSystemSettings } from "@/hooks/useSystemSettings";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import {
    AlertCircle,
    Database,
    Gamepad2,
    Settings,
    Shield,
    Lock,
    Unlock,
    Bell,
} from "lucide-react";

function BoolBadge({ ok, trueLabel, falseLabel }: { ok: boolean; trueLabel: string; falseLabel: string }) {
    return ok ? (
        <Badge className="bg-green-500/10 text-green-500 border-green-500/20">{trueLabel}</Badge>
    ) : (
        <Badge variant="destructive">{falseLabel}</Badge>
    );
}

function KV({ k, v }: { k: string; v: ReactNode }) {
    return (
        <div className="flex items-start justify-between gap-4 py-2 border-b last:border-b-0">
            <div className="text-sm text-muted-foreground">{k}</div>
            <div className="text-sm font-medium text-right break-all">{v}</div>
        </div>
    );
}

export default function SystemSettingsPage() {
    const { user, logout } = useAuthContext();
    const { settings, isLoading, error, actionLoading, setMaintenanceMode, refresh } = useSystemSettings();

    const maintenanceEnabled = Boolean(settings?.system?.maintenance_mode);
    const foundersRolesCount = settings?.system?.founders_roles_ids?.length || 0;

    const corsCount = useMemo(() => {
        const list = settings?.security?.cors_origins || [];
        return Array.isArray(list) ? list.length : 0;
    }, [settings]);

    const handleLogout = () => {
        void logout();
    };

    return (
        <AdminLayout user={user} onLogout={handleLogout}>
            <div className="space-y-6 sm:space-y-8">
                <EnterprisePageHeader
                    badge="Settings"
                    title="System Settings"
                    subtitle="Configuration is read-only. Only maintenance mode can be toggled."
                    icon={<Settings className="h-6 w-6" />}
                    isLoading={isLoading}
                    onRefresh={refresh}
                />

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 sm:gap-6">
                    <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    {maintenanceEnabled ? (
                                        <Lock className="h-4 w-4 text-orange-500" />
                                    ) : (
                                        <Unlock className="h-4 w-4 text-green-500" />
                                    )}
                                    Maintenance
                                </div>
                                {maintenanceEnabled ? (
                                    <Badge variant="destructive" className="text-xs">
                                        Active
                                    </Badge>
                                ) : (
                                    <Badge variant="outline" className="text-xs">
                                        Normal
                                    </Badge>
                                )}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <Button
                                onClick={async () => {
                                    const res = await setMaintenanceMode(!maintenanceEnabled);
                                    if (!res.success) console.error(res.error);
                                }}
                                disabled={actionLoading === "maintenance" || !settings}
                                variant={maintenanceEnabled ? "destructive" : "outline"}
                                className="w-full"
                            >
                                {maintenanceEnabled ? "Disable Maintenance" : "Enable Maintenance"}
                            </Button>
                            <div className="text-xs text-muted-foreground">
                                Persisted in DB (safe toggle). Use for planned downtime only.
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Shield className="h-4 w-4 text-blue-500" />
                                    Security
                                </div>
                                <Badge variant="outline" className="text-xs">
                                    JWT
                                </Badge>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            <div className="text-lg font-bold text-foreground">{settings?.security?.jwt_algorithm || "-"}</div>
                            <div className="text-sm text-muted-foreground">{corsCount} CORS origins</div>
                        </CardContent>
                    </Card>

                    <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Gamepad2 className="h-4 w-4 text-purple-500" />
                                    Gaming
                                </div>
                                <Badge variant="outline" className="text-xs">
                                    Zomboid
                                </Badge>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            <div className="text-lg font-bold text-foreground">
                                {settings?.gaming?.zomboid?.rcon_host || "-"}:{settings?.gaming?.zomboid?.rcon_port ?? "-"}
                            </div>
                            <div className="text-sm text-muted-foreground">Game port {settings?.gaming?.zomboid?.game_port ?? "-"}</div>
                        </CardContent>
                    </Card>

                    <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Database className="h-4 w-4 text-green-500" />
                                    Admin Auth
                                </div>
                                <Badge variant="outline" className="text-xs">
                                    Discord Roles
                                </Badge>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            <div className="text-lg font-bold text-foreground">{foundersRolesCount}</div>
                            <div className="text-sm text-muted-foreground">founder role IDs configured</div>
                        </CardContent>
                    </Card>
                </div>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
                    <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Settings className="h-5 w-5 text-primary" />
                                Configuration
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Tabs defaultValue="system" className="w-full">
                                <TabsList className="grid h-auto w-full grid-cols-2 gap-1 sm:grid-cols-3 lg:grid-cols-5">
                                    <TabsTrigger value="system" className="text-xs sm:text-sm">System</TabsTrigger>
                                    <TabsTrigger value="security" className="text-xs sm:text-sm">Security</TabsTrigger>
                                    <TabsTrigger value="gaming" className="text-xs sm:text-sm">Gaming</TabsTrigger>
                                    <TabsTrigger value="backup" className="text-xs sm:text-sm">Backup</TabsTrigger>
                                    <TabsTrigger value="notify" className="text-xs sm:text-sm">Notify</TabsTrigger>
                                </TabsList>

                                <TabsContent value="system" className="pt-4">
                                    <ScrollArea className="h-80">
                                        <div className="space-y-2">
                                            <KV k="Project" v={settings?.system?.project_name || "-"} />
                                            <KV k="Environment" v={settings?.system?.environment || "-"} />
                                            <KV k="Version" v={settings?.system?.version || "-"} />
                                            <KV k="Frontend URL" v={<code className="font-mono text-xs">{settings?.system?.frontend_url || "-"}</code>} />
                                            <KV k="Backend URL" v={<code className="font-mono text-xs">{settings?.system?.backend_url || "-"}</code>} />
                                            <KV k="Discord Guild ID" v={<code className="font-mono text-xs">{settings?.system?.discord_guild_id || "-"}</code>} />
                                            <KV k="Founders Role IDs" v={<code className="font-mono text-xs">{(settings?.system?.founders_roles_ids || []).join(", ") || "-"}</code>} />
                                            <KV k="Rate limit (per minute)" v={String(settings?.system?.rate_limit_per_minute ?? "-")} />
                                        </div>
                                    </ScrollArea>
                                </TabsContent>

                                <TabsContent value="security" className="pt-4">
                                    <ScrollArea className="h-80">
                                        <div className="space-y-2">
                                            <KV k="JWT algorithm" v={settings?.security?.jwt_algorithm || "-"} />
                                            <KV k="Access token (minutes)" v={String(settings?.security?.jwt_access_token_expire_minutes ?? "-")} />
                                            <KV k="Refresh token (days)" v={String(settings?.security?.jwt_refresh_token_expire_days ?? "-")} />
                                            <KV k="Internal token" v={<BoolBadge ok={Boolean(settings?.security?.internal_token_configured)} trueLabel="Configured" falseLabel="Missing" />} />
                                            <KV k="Discord OAuth" v={<BoolBadge ok={Boolean(settings?.security?.discord_oauth_configured)} trueLabel="Configured" falseLabel="Missing" />} />
                                            <KV k="CORS origins" v={String(corsCount)} />
                                            <div className="pt-2 text-xs text-muted-foreground">
                                                CORS origins are environment-backed. Secrets are never shown in the backoffice.
                                            </div>
                                        </div>
                                    </ScrollArea>
                                </TabsContent>

                                <TabsContent value="gaming" className="pt-4">
                                    <ScrollArea className="h-80">
                                        <div className="space-y-2">
                                            <KV k="Zomboid RCON host" v={settings?.gaming?.zomboid?.rcon_host || "-"} />
                                            <KV k="Zomboid RCON port" v={String(settings?.gaming?.zomboid?.rcon_port ?? "-")} />
                                            <KV k="Zomboid game port" v={String(settings?.gaming?.zomboid?.game_port ?? "-")} />
                                            <KV k="Zomboid RCON password" v={<BoolBadge ok={Boolean(settings?.gaming?.zomboid?.rcon_password_configured)} trueLabel="Configured" falseLabel="Missing" />} />
                                        </div>
                                    </ScrollArea>
                                </TabsContent>

                                <TabsContent value="backup" className="pt-4">
                                    <ScrollArea className="h-80">
                                        <div className="space-y-2">
                                            <KV k="R2 endpoint" v={<BoolBadge ok={Boolean(settings?.backup?.r2?.endpoint_configured)} trueLabel="Configured" falseLabel="Missing" />} />
                                            <KV k="R2 bucket" v={<BoolBadge ok={Boolean(settings?.backup?.r2?.bucket_configured)} trueLabel="Configured" falseLabel="Missing" />} />
                                            <KV k="R2 access key" v={<BoolBadge ok={Boolean(settings?.backup?.r2?.access_key_configured)} trueLabel="Configured" falseLabel="Missing" />} />
                                            <KV k="R2 secret key" v={<BoolBadge ok={Boolean(settings?.backup?.r2?.secret_key_configured)} trueLabel="Configured" falseLabel="Missing" />} />
                                        </div>
                                    </ScrollArea>
                                </TabsContent>

                                <TabsContent value="notify" className="pt-4">
                                    <ScrollArea className="h-80">
                                        <div className="space-y-2">
                                            <KV k="Discord webhook" v={<BoolBadge ok={Boolean(settings?.notifications?.discord_webhook_configured)} trueLabel="Configured" falseLabel="Missing" />} />
                                            <KV k="KSP webhook" v={<BoolBadge ok={Boolean(settings?.notifications?.ksp_discord_webhook_configured)} trueLabel="Configured" falseLabel="Missing" />} />
                                        </div>
                                    </ScrollArea>
                                </TabsContent>
                            </Tabs>
                        </CardContent>
                    </Card>

                    <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Bell className="h-5 w-5 text-primary" />
                                Notes
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3 text-sm text-muted-foreground">
                            <div>
                                Admin access is enforced by Discord role IDs from `FOUNDERS_ROLES_IDS`.
                            </div>
                            <div>
                                Maintenance mode is persisted server-side and is suitable for planned downtime.
                            </div>
                            <div>
                                If you need feature flags or environment editing, they should be implemented as explicit,
                                audited, persisted settings with strong validation (not raw env writes).
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {error && (
                    <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                                <AlertCircle className="h-4 w-4" />
                                <span className="font-medium">Connection error</span>
                            </div>
                            <p className="text-sm text-red-600 dark:text-red-400 mt-1">{error}</p>
                        </CardContent>
                    </Card>
                )}
            </div>
        </AdminLayout>
    );
}
